import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConnectionType, IChildConnectionDTO } from '../../../../../api/connections.dao';
import { Popover } from '../../../../global/popover/popover';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { composeChildConnectionDetailsCopyAllMessage } from '../../../../../infrastructure/utils/connections.utils';
import cln from 'classnames';
import './child-connection-details-popover.scss';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { DELETE_TEXT, EDIT_TEXT, MORE_BUTTON_TEXT } from '../../../../../constants/common-labels.constants';
import OptionsButton from '../../../../global/options-button/options-button';
import { UniqueJoiningLinkDetails } from './child-connection-details-content/unique-joining-link-details';
import { UniqueDialInDetails } from './child-connection-details-content/unique-dial-in-details';
import { PanelistLinkDetails } from './child-connection-details-content/panelist-link-details';

export interface IChildConnectionDetailsPopoverProps {
    childConnection: IChildConnectionDTO;
    isPopoverOpen: boolean;
    setIsPopoverOpen: (isPopoverOpen: boolean) => void;
    handleTrackChildCopyBtn?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, type: string) => void;
    listItemRef: React.MutableRefObject<HTMLElement>;
    openChildConnectionModal: (childConnection: IChildConnectionDTO, parentConnectionId: number) => void;
    deleteChildConnection: (childConnection: IChildConnectionDTO, parentConnectionId: number) => void;
    parentConnectionId: number;
    propsCustomLeft?: number;
}

const ChildConnectionDetailsPopover = (props: IChildConnectionDetailsPopoverProps): JSX.Element => {
    const intl = useIntl();
    const {
        childConnection,
        isPopoverOpen,
        setIsPopoverOpen,
        handleTrackChildCopyBtn,
        listItemRef,
        openChildConnectionModal,
        parentConnectionId,
        deleteChildConnection,
        propsCustomLeft,
    } = props;
    const copyAllMessage = composeChildConnectionDetailsCopyAllMessage(childConnection);
    const customWidth = 225;
    const customLeft = propsCustomLeft || 110;
    const connectionManagementPermission = hasPermission(Permission.CONNECTIONS_MANAGEMENT);

    const onCopyBtnClick = (): void => {
        if (handleTrackChildCopyBtn) {
            handleTrackChildCopyBtn(CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.COPY_DATA, childConnection.type);
        }
    };

    const options = [
        connectionManagementPermission && {
            label: intl.formatMessage(EDIT_TEXT),
            action: () => {
                openChildConnectionModal(childConnection, parentConnectionId);
                setIsPopoverOpen(false);
            }
        },
        connectionManagementPermission && {
            label: intl.formatMessage(DELETE_TEXT),
            danger: true,
            action: () => {
                deleteChildConnection(childConnection, parentConnectionId);
                setIsPopoverOpen(false);
            }
        }
    ].filter(Boolean);

    return (
        <Popover
            isOpen={isPopoverOpen}
            customWidth={customWidth}
            customLeft={customLeft}
            buttonRef={listItemRef}
            onClickOutside={() => setIsPopoverOpen(false)}
            align='start'
        >
            <div className='child-connection-list' onClick={e => e.stopPropagation()}>
                <div className='child-connection-list__popover'>
                    {
                        childConnection.type === ConnectionType.UniqueJoiningLink
                            ? <UniqueJoiningLinkDetails childConnection={childConnection} onCopyBtnClick={onCopyBtnClick}/>
                            : childConnection.type === ConnectionType.PanelistLink
                            ? <PanelistLinkDetails childConnection={childConnection} onCopyBtnClick={onCopyBtnClick} />
                            : <UniqueDialInDetails childConnection={childConnection} onCopyBtnClick={onCopyBtnClick} />
                    }
                </div>
                <div className={cln('child-connection-list__popover__footer', { 'buttons-block': !!options.length })}>
                    {
                        !!options.length &&
                        <OptionsButton
                            className='small-button'
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={options}
                            position='top'
                            align='start'
                        />
                    }
                    <div className='child-connection__copy-all-wrapper'>
                        <CopyButton
                            className='child-connection__copy-all small-button copy-all'
                            text={
                                <FormattedMessage
                                    id='oe.common.copyAll'
                                    description='Button text'
                                    defaultMessage='Copy all'
                                />
                            }
                            copyValue={copyAllMessage}
                            onCopy={onCopyBtnClick}
                            withoutIcon
                        />
                    </div>
                </div>
            </div>
        </Popover>
    );
};

export { ChildConnectionDetailsPopover };
